/**
 * delegationsApi.ts — API client for delegation inbox queries.
 * 
 * Auth-inferred current user (no userId param).
 * API extracts user ID from token and returns filtered delegations.
 * 
 * Uses apiClient for automatic MSAL token injection.
 */

import apiClient from '../services/apiClient';
import { DelegationSummary, PendingUpdateSummary, UpdateRequest } from '../types/delegation';

/**
 * Get delegations assigned to current user.
 * 
 * @param status - Status filter: 'Active' (default), or comma-separated like 'Completed,Recalled'
 * @returns List of delegations where delegatedToUserId == currentUser
 */
export async function getDelegatedToMe(
  status: string = 'Active'
): Promise<DelegationSummary[]> {
  try {
    const response = await apiClient.get('/api/delegations/by-delegate', {
      params: { status },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || `Failed to fetch delegations: ${error.message}`);
  }
}

/**
 * Get delegations assigned by current user.
 * 
 * @param status - Status filter: 'Active' (default), or comma-separated
 * @returns List of delegations where delegatedByUserId == currentUser
 */
export async function getDelegatedByMe(
  status: string = 'Active'
): Promise<DelegationSummary[]> {
  try {
    const response = await apiClient.get('/api/delegations/by-delegator', {
      params: { status },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || `Failed to fetch delegations: ${error.message}`);
  }
}

/**
 * Get pending update requests for current user.
 * Updates awaiting response from the delegate.
 * 
 * @returns List of update requests with status 'Pending' where delegatedToUserId == currentUser
 */
export async function getPendingUpdates(): Promise<PendingUpdateSummary[]> {
  try {
    const response = await apiClient.get('/api/delegations/pending-updates');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || `Failed to fetch pending updates: ${error.message}`);
  }
}

/**
 * Get completed/recalled delegations for current user.
 * 
 * @returns List of delegations with status in Completed, Recalled, Cancelled
 */
export async function getCompletedDelegations(): Promise<DelegationSummary[]> {
  try {
    const response = await apiClient.get('/api/delegations/by-delegate', {
      params: { status: 'Completed,Recalled,Cancelled' },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || `Failed to fetch completed delegations: ${error.message}`);
  }
}

/**
 * Get single delegation by ID (for detail page).
 *
 * @param delegationId - ID of delegation
 * @returns Full delegation details with visibility check
 */
export async function getDelegationDetail(externalId: string): Promise<DelegationSummary> {
  try {
    const response = await apiClient.get(`/api/delegations/${externalId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || `Failed to fetch delegation: ${error.message}`);
  }
}

export async function getAwaitingResponse(): Promise<PendingUpdateSummary[]> {
  try {
    const response = await apiClient.get('/api/delegations/awaiting-response');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || `Failed to fetch awaiting-response list: ${error.message}`);
  }
}

export async function getDelegationUpdateRequests(delegationGuid: string): Promise<UpdateRequest[]> {
  try {
    const response = await apiClient.get(`/api/delegations/${delegationGuid}/update-requests`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || `Failed to fetch update requests: ${error.message}`);
  }
}

export async function completeDelegation(delegationId: number): Promise<void> {
  try {
    await apiClient.post(`/api/delegations/${delegationId}/complete`);
  } catch (error: any) {
    throw new Error(error.response?.data || `Failed to complete delegation: ${error.message}`);
  }
}

export async function recallDelegation(delegationId: number, recallReason: string): Promise<void> {
  try {
    await apiClient.post(`/api/delegations/${delegationId}/recall`, { recallReason });
  } catch (error: any) {
    throw new Error(error.response?.data || `Failed to recall delegation: ${error.message}`);
  }
}

export async function requestDelegationUpdate(
  delegationId: number,
  message?: string,
  dueBy?: string
): Promise<void> {
  try {
    await apiClient.post(`/api/delegations/${delegationId}/request-update`, { message, dueBy });
  } catch (error: any) {
    throw new Error(error.response?.data || `Failed to request update: ${error.message}`);
  }
}

export async function respondToUpdate(updateRequestId: number, responseText: string): Promise<void> {
  try {
    await apiClient.post(`/api/update-requests/${updateRequestId}/respond`, { responseText });
  } catch (error: any) {
    throw new Error(error.response?.data || `Failed to respond to update: ${error.message}`);
  }
}
